
import AdminLayout from "@/components/layout/AdminLayout";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CollectionGrid } from "@/components/collections/CollectionGrid";
import { useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Command, CommandInput, CommandList, CommandEmpty } from "@/components/ui/command";

type VisibilityFilter = 'all' | 'public' | 'private';

export default function Collections() {
  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>('all');
  const [titleFilter, setTitleFilter] = useState('');
  const [selectedCreatorId, setSelectedCreatorId] = useState<string>('all');
  const [creatorSearch, setCreatorSearch] = useState('');

  // First fetch creator IDs that have collections, then fetch their details
  const { data: creators } = useQuery({
    queryKey: ['creators-with-collections'],
    queryFn: async () => {
      // Get unique creator IDs from collections
      const { data: uniqueCreatorIds, error: collectionError } = await supabase
        .from('collections')
        .select('creator_id')
        .not('creator_id', 'is', null);

      if (collectionError) throw collectionError;

      // Create a Set to get unique creator IDs
      const creatorIds = [...new Set(uniqueCreatorIds.map(item => item.creator_id))];

      // Then, fetch the details for these creators
      const { data: creatorDetails, error: creatorsError } = await supabase
        .from('creators')
        .select('creator_id, name, surname, nick_name')
        .in('creator_id', creatorIds)
        .order('surname');

      if (creatorsError) throw creatorsError;
      return creatorDetails;
    },
  });

  // Debounce title changes to prevent re-renders
  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTitleFilter(e.target.value);
  }, []);

  const getArtistDisplayName = (creator: { name: string; surname: string; nick_name: string | null }) => {
    if (creator?.nick_name) {
      return `${creator.name} ${creator.surname} (${creator.nick_name})`;
    }
    return `${creator.name} ${creator.surname}`;
  };

  const filteredCreators = creators?.filter(creator => {
    const searchTerm = creatorSearch.toLowerCase();
    const fullName = getArtistDisplayName(creator).toLowerCase();
    return fullName.includes(searchTerm);
  });

  const { data: collections, isLoading } = useQuery({
    queryKey: ['collections', visibilityFilter, titleFilter, selectedCreatorId],
    queryFn: async () => {
      let query = supabase
        .from('collections')
        .select(`
          *,
          creator:creator_id (
            name,
            surname
          ),
          collection_artworks (
            artwork:artwork_id (
              artwork_id,
              title,
              preview
            )
          )
        `)
        .order('created_at', { ascending: false });

      // Apply visibility filter
      if (visibilityFilter === 'public') {
        query = query.eq('is_private', false);
      } else if (visibilityFilter === 'private') {
        query = query.eq('is_private', true);
      }

      // Apply title filter
      if (titleFilter) {
        query = query.ilike('title', `%${titleFilter}%`);
      }

      // Apply creator filter
      if (selectedCreatorId !== 'all') {
        query = query.eq('creator_id', parseInt(selectedCreatorId, 10));
      }

      const { data, error } = await query;

      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="container mx-auto py-6">
          <div className="h-[200px] flex items-center justify-center">
            <p className="text-muted-foreground">Loading collections...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-semibold tracking-tight">Collections</h1>
          <Select
            value={visibilityFilter}
            onValueChange={(value) => setVisibilityFilter(value as VisibilityFilter)}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Filter collections" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Collections</SelectItem>
              <SelectItem value="public">Public Only</SelectItem>
              <SelectItem value="private">Private Only</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="Filter by title..."
            value={titleFilter}
            onChange={handleTitleChange}
            className="w-[200px]"
          />
          <Select
            value={selectedCreatorId}
            onValueChange={setSelectedCreatorId}
          >
            <SelectTrigger className="w-[250px]">
              <SelectValue placeholder="Filter by creator" />
            </SelectTrigger>
            <SelectContent>
              <Command>
                <CommandInput 
                  placeholder="Search creators..."
                  value={creatorSearch}
                  onValueChange={setCreatorSearch}
                />
                <CommandList>
                  <CommandEmpty>No creators found.</CommandEmpty>
                  <SelectItem value="all">All creators</SelectItem>
                  {filteredCreators?.map((creator) => (
                    <SelectItem 
                      key={creator.creator_id} 
                      value={String(creator.creator_id)}
                    >
                      {getArtistDisplayName(creator)}
                    </SelectItem>
                  ))}
                </CommandList>
              </Command>
            </SelectContent>
          </Select>
        </div>
        <CollectionGrid collections={collections || []} />
      </div>
    </AdminLayout>
  );
}
